import type { OrderSupplyAllocation, OrderSupplyLine, OrderSupplyResponse, OrderTransferSource } from '../order.service';
import { deliveryDeadline } from './delivery-deadline.util';
import type { ProductDeliveryReference } from './order-product-delivery.util';

export type OrderSupplyTone = 'ready' | 'progress' | 'attention' | 'overdue' | 'muted';

export interface OrderSupplyProduct extends ProductDeliveryReference {
  provider?: string;
  description?: string;
  article?: string;
  brand?: string;
}

export interface OrderSupplySourceView {
  id: string;
  kind: 'warehouse' | 'supplier' | 'transfer' | 'uncovered' | 'unknown';
  name: string;
  quantity: number;
  tone: OrderSupplyTone;
  icon: string;
  status: string;
  title: string;
}

const quantity = (value: unknown) => Math.max(Number(value) || 0, 0);
const EPSILON = 0.0001;

export function findOrderSupplyLine<T extends OrderSupplyLine>(
  product: OrderSupplyProduct,
  supply?: { lines: T[] }
): T | undefined {
  // A missing/mismatched row ID must never borrow a different row of the same SKU.
  if (product.orderProductId) {
    return supply?.lines.find(line => Number(line.order_product_id) === Number(product.orderProductId));
  }
  const matches = supply?.lines.filter(line => Number(line.product_id) === Number(product.productId)) || [];
  return matches.length === 1 ? matches[0] : undefined;
}

export function supplierSourceState(allocation: OrderSupplyAllocation, now = Date.now()) {
  const deadline = deliveryDeadline(allocation.expected_at, now, allocation.expected_at_precision);
  const planned = allocation.status === 'PLANNED';
  const received = quantity(allocation.physically_received_quantity ?? allocation.received_quantity);
  const complete = quantity(allocation.quantity) - received <= EPSILON;
  const awaitingDocuments = complete && quantity(allocation.received_quantity) + EPSILON < quantity(allocation.quantity);
  const cancelled = allocation.status === 'CANCELLED';
  const tone: OrderSupplyTone = cancelled || complete ? 'muted' : planned || !deadline.date
    ? 'attention' : deadline.tone === 'overdue' ? 'overdue' : 'progress';
  const status = cancelled ? 'Отменено' : awaitingDocuments ? 'Получено · оформление документов' : complete ? 'Получено' : planned ? (allocation.needs_order ? 'Нужно заказать' : 'Не заказано')
    : !deadline.date ? 'Срок не указан' : deadline.tone === 'overdue' ? 'Поставка просрочена'
    : `Ожидается · ${deadline.label.toLowerCase()}`;
  return { tone, status, received, date: deadline.date, icon: complete ? 'check' : planned ? 'edit_calendar' : 'local_shipping' };
}

export function transferSourceState(transfer: OrderTransferSource) {
  const labels: Record<string, string> = {
    DRAFT: 'Ожидает сборки', PICKING: 'Собирается', READY_TO_DISPATCH: 'Готово к отправке',
    IN_TRANSIT: 'В пути', PARTIALLY_RECEIVED: 'Принято частично', RECEIVED: 'Принято',
    ACCEPTED: 'Принято', CANCELLED: 'Отменено'
  };
  return labels[transfer.status] || 'Ожидается перемещение';
}

export function orderSupplySources(
  product: OrderSupplyProduct,
  supply?: OrderSupplyResponse,
  cancelled = false,
  now = Date.now(),
  unavailable = false
): OrderSupplySourceView[] {
  const line = findOrderSupplyLine(product, supply);
  const required = quantity(line?.required ?? product.quan);
  const fulfilled = quantity(line?.fulfilled ?? product.shipped_quan);
  const remaining = Math.max(required - fulfilled, 0);
  const warehouse = line?.warehouse_name || 'Склад заказа';
  const terminal = cancelled || required > 0 && remaining <= EPSILON;
  if (terminal) {
    const status = cancelled ? 'Заказ отменён' : 'Отгружено';
    const name = line ? warehouse : product.provider || 'Источник';
    return [{ id: line ? 'warehouse' : 'unknown', kind: line ? 'warehouse' : 'unknown', name, quantity: required, tone: 'muted',
      icon: cancelled ? 'block' : 'done_all', status, title: `${name} · ${status}` }];
  }
  if (!line) {
    const status = unavailable ? 'Не удалось загрузить источник' : supply ? 'Данные строки недоступны' : 'Загрузка источника';
    return [{ id: 'unknown', kind: 'unknown', name: product.provider || 'Источник', quantity: remaining,
      tone: 'muted', icon: unavailable ? 'error_outline' : 'info_outline', status,
      title: `${status}. Открыть информацию о поставке` }];
  }

  const sources: OrderSupplySourceView[] = [];
  // Receipts have already become local reservations. Only outstanding external
  // quantities are additional sources; counting received again would duplicate them.
  const reserved = Math.min(quantity(line.reserved), remaining);
  if (reserved > EPSILON) {
    const picked = Math.min(quantity(line.ready_quantity ?? (line.workflow_version === 1 ? line.placed_quantity : line.picked)), reserved);
    const inHands = quantity((line as OrderSupplyLine & { in_hands_quantity?: number }).in_hands_quantity);
    const ready = picked + EPSILON >= reserved;
    const status = ready ? 'Готово к выдаче' : inHands > EPSILON ? `Отбор в работе · в руках ${inHands} шт.` : picked > EPSILON
      ? `${line.workflow_version === 1 ? 'На полке заказа' : 'Отобрано'} ${picked} из ${reserved} шт.`
      : line.workflow_version === 1 && quantity(line.picked) > EPSILON ? 'Ожидает размещения' : 'Ожидает отбора';
    sources.push({ id: 'warehouse', kind: 'warehouse', name: warehouse, quantity: reserved,
      tone: ready ? 'ready' : 'progress', icon: ready ? 'task_alt' : 'inventory_2', status,
      title: `${warehouse} · ${reserved} шт. · ${status}` });
  }
  const externalSources: OrderSupplySourceView[] = [];
  for (const allocation of line.allocations || []) {
    const outstanding = Math.max(quantity(allocation.quantity) - quantity(allocation.physically_received_quantity ?? allocation.received_quantity), 0);
    if (allocation.status === 'CANCELLED' || allocation.status === 'RECEIVED' || outstanding <= EPSILON) continue;
    const state = supplierSourceState(allocation, now);
    const name = allocation.provider || 'Поставщик';
    externalSources.push({ id: `supplier-${allocation.id}`, kind: 'supplier', name, quantity: outstanding,
      tone: state.tone, icon: state.icon, status: state.status,
      title: `${name} · ${outstanding} шт. · ${state.status}${state.date ? ` · ${state.date.full}` : ''}` });
  }
  let transfers = 0;
  for (const transfer of line.transfer_sources || []) {
    const outstanding = Math.max(quantity(transfer.quantity) - quantity(transfer.received_quantity), 0);
    if (transfer.status === 'CANCELLED' || outstanding <= EPSILON) continue;
    transfers += outstanding;
    const name = transfer.source_warehouse || 'Другой склад';
    const status = `${transferSourceState(transfer)} · срок не указан`;
    externalSources.push({ id: `transfer-${transfer.moving_doc_id}`, kind: 'transfer', name, quantity: outstanding,
      tone: 'attention', icon: 'local_shipping', status,
      title: `${name} → ${transfer.target_warehouse || warehouse} · ${outstanding} шт. · ${status}` });
  }
  const unassignedTransfer = Math.max(quantity(line.transfer_pending) - transfers, 0);
  if (unassignedTransfer > EPSILON) {
    externalSources.push({ id: 'transfer-pending', kind: 'transfer', name: 'Перемещение', quantity: unassignedTransfer,
      tone: 'attention', icon: 'local_shipping', status: 'Документ перемещения не оформлен',
      title: `Ожидается перемещение · ${unassignedTransfer} шт. · документ и срок не указаны` });
  }
  const externalQuantity = externalSources.reduce((sum, source) => sum + source.quantity, 0);
  const externalRequired = Math.max(remaining - reserved, 0);
  if (externalQuantity > externalRequired + EPSILON) {
    // Goods can be physically received before document reconciliation, sometimes
    // from a replacement supplier. Do not guess which supplier still owes goods.
    if (externalRequired > EPSILON) sources.push({ id: 'source-pending', kind: 'unknown', name: 'Источник уточняется', quantity: externalRequired,
      tone: 'attention', icon: 'manage_search', status: 'Поставка и документы требуют сверки',
      title: `Осталось обеспечить ${externalRequired} шт. · полученный товар ещё не сопоставлен с источниками` });
  } else {
    sources.push(...externalSources);
  }
  const uncovered = Math.max(remaining - sources.reduce((sum, source) => sum + source.quantity, 0), 0);
  if (uncovered > EPSILON) {
    sources.push({ id: 'uncovered', kind: 'uncovered', name: 'Источник не выбран', quantity: uncovered,
      tone: 'attention', icon: 'warning_amber', status: 'Не обеспечено',
      title: `Не обеспечено ${uncovered} шт. · требуется выбрать источник` });
  }
  return sources.length ? sources : [{ id: 'unknown', kind: 'unknown', name: warehouse, quantity: remaining,
    tone: 'attention', icon: 'help_outline', status: 'Требуется проверить обеспечение',
    title: 'Поставка получена, но резерв или отбор по строке не подтверждены' }];
}
